/**
 * safeStorage — the ONE way the web app touches `localStorage` / `sessionStorage`.
 *
 * WHY: Safari and Firefox throw `SecurityError: The operation is insecure.` on
 * the bare property read of `localStorage` when the page runs in a third-party
 * iframe whose storage the browser blocks — the public widget embed
 * (`/embed/:slug/:widget`) is exactly that page. A `try` around `getItem` does
 * not help once the `localStorage` reference was evaluated outside it: the
 * 2026-10-22 crash digest had a default parameter (`storage = … localStorage …`)
 * throw from a hook above the root ErrorBoundary, and the embed rendered as an
 * empty iframe. Here the global is looked up inside the try, on every call.
 *
 * No method ever throws. Every other file goes through this module;
 * `__tests__/storageAccessGate.test.js` fails on a raw `localStorage` /
 * `sessionStorage` anywhere else in `src/`.
 *
 * @see docs/spec/00-overview.md — "Web Storage access (`safeStorage`)"
 */

const PROBE_KEY = "sendly-storage-probe";

/**
 * A storage area over whatever `resolve()` returns. `resolve` is called on
 * every operation (never cached), inside the try — so a test can stub the
 * global, and a blocked storage costs one caught exception, not a crash.
 * @param {() => Storage | null | undefined} resolve
 */
export function makeStorageArea(resolve) {
  const area = {
    /** The stored string, or null (missing key, no storage, blocked). */
    get(key) {
      try {
        const s = resolve();
        if (!s) return null;
        const v = s.getItem(key);
        return v === undefined ? null : v;
      } catch {
        return null;
      }
    },
    /** true when written; false when the storage is missing, blocked or full. */
    set(key, value) {
      try {
        const s = resolve();
        if (!s) return false;
        s.setItem(key, String(value));
        return true;
      } catch {
        return false;
      }
    },
    /** true when the remove call went through (also for a key that was not there). */
    remove(key) {
      try {
        const s = resolve();
        if (!s) return false;
        s.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
    /** The parsed value, or `fallback` when missing, unreadable or not JSON. */
    getJSON(key, fallback = null) {
      const raw = area.get(key);
      if (raw === null) return fallback;
      try {
        return JSON.parse(raw);
      } catch {
        return fallback;
      }
    },
    /** `set(key, JSON.stringify(value))`; false also when the value cannot be serialised. */
    setJSON(key, value) {
      let raw;
      try {
        raw = JSON.stringify(value);
      } catch {
        return false;
      }
      return raw === undefined ? false : area.set(key, raw);
    },
    /** Every key name; [] when the storage is missing or blocked. */
    keys() {
      try {
        const s = resolve();
        if (!s) return [];
        const out = [];
        for (let i = 0; i < s.length; i++) {
          const k = s.key(i);
          if (k !== null) out.push(k);
        }
        return out;
      } catch {
        return [];
      }
    },
    /** true only when a probe write + remove succeeds (readable is not enough: quota / Safari private mode). */
    available() {
      return area.set(PROBE_KEY, "1") && area.remove(PROBE_KEY);
    },
  };
  return area;
}

export const localStore = makeStorageArea(() => globalThis.localStorage);
export const sessionStore = makeStorageArea(() => globalThis.sessionStorage);
